import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
} from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { RootState } from "../../store";
import { fetchPermissions, Permission } from "../Permissions/permissionsSlice";
import {
  createRole,
  deleteRole,
  fetchRoles,
  Role,
  updateRoleAction,
} from "./rolesSlice";

const Roles: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { roles, loading, error } = useSelector(
    (state: RootState) => state.roles
  );
  const { permissions } = useSelector((state: RootState) => state.permissions);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    displayName: "",
    description: "",
    permissions: [] as string[],
    isActive: true,
  });

  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchPermissions());
  }, [dispatch]);

  const handleOpenDialog = (role?: Role) => {
    // Only allow editing admin and editor roles
    if (role && role.name !== "admin" && role.name !== "editor") {
      toast.error("Only admin and editor roles can be edited");
      return;
    }

    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name,
        displayName: role.displayName,
        description: role.description || "",
        permissions: Array.isArray(role.permissions)
          ? role.permissions.map((p: any) =>
              typeof p === "string" ? p : p._id || p.id
            )
          : [],
        isActive: role.isActive,
      });
    } else {
      setEditingRole(null);
      setFormData({
        name: "",
        displayName: "",
        description: "",
        permissions: [],
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRole(null);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.displayName) {
      toast.error("Name and Display Name are required");
      return;
    }

    try {
      if (editingRole) {
        const id = editingRole._id || editingRole.id;
        if (id) {
          dispatch(updateRoleAction({ id, data: formData }));
        }
      } else {
        dispatch(createRole(formData));
      }
      handleCloseDialog();
      setTimeout(() => dispatch(fetchRoles()), 500);
    } catch (error) {
      console.error("Failed to save role:", error);
    }
  };

  const handleDelete = async (role: Role) => {
    // Only allow deleting non-system roles (not user, super admin, admin, editor)
    if (
      role.isSystemRole ||
      role.name === "user" ||
      role.name === "super admin" ||
      role.name === "admin" ||
      role.name === "editor"
    ) {
      toast.error("Cannot delete this role");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete "${role.displayName}"? This action cannot be undone.`
      )
    ) {
      const id = role._id || role.id;
      if (id) {
        dispatch(deleteRole(id));
        setTimeout(() => dispatch(fetchRoles()), 500);
      }
    }
  };

  if (loading && roles.length === 0) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant='h4' fontWeight='700' gutterBottom>
            Role Management
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const formatDate = (date: number | string | Date | undefined) => {
    if (!date) return "N/A";
    const dateObj = typeof date === 'number' ? new Date(date) : new Date(date);
    if (isNaN(dateObj.getTime())) return "N/A";
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main" }}>
            <SecurityIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Typography variant='body2' fontWeight='600'>
            {params.row.displayName || params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant='body2' color='text.secondary' noWrap>
          {params.value || "No description"}
        </Typography>
      ),
    },
    {
      field: "permissions",
      headerName: "Permissions",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const perms = params.value || [];
        const count = Array.isArray(perms) ? perms.length : 0;
        return (
          <Chip
            label={`${count} permission${count !== 1 ? "s" : ""}`}
            size='small'
            color='primary'
            variant='outlined'
          />
        );
      },
    },
    {
      field: "isActive",
      headerName: "Status",
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          color={params.value ? "success" : "default"}
          size='small'
        />
      ),
    },
    {
      field: "isSystemRole",
      headerName: "Type",
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? "System" : "Custom"}
          color={params.value ? "warning" : "info"}
          size='small'
          variant='outlined'
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 120,
      getActions: (params) => {
        // Only admin and editor roles can be edited
        const canEdit =
          params.row.name === "admin" || params.row.name === "editor";
        // Cannot delete system roles or admin/editor
        const canDelete =
          !params.row.isSystemRole &&
          params.row.name !== "user" &&
          params.row.name !== "super admin" &&
          params.row.name !== "admin" &&
          params.row.name !== "editor";

        return [
          <GridActionsCellItem
            icon={
              <Tooltip
                title={
                  canEdit
                    ? "Edit Role"
                    : "Only admin and editor roles can be edited"
                }
              >
                <EditIcon />
              </Tooltip>
            }
            label='Edit'
            onClick={() => handleOpenDialog(params.row)}
            disabled={!canEdit}
          />,
          <GridActionsCellItem
            icon={
              <Tooltip
                title={canDelete ? "Delete Role" : "Cannot delete this role"}
              >
                <DeleteIcon />
              </Tooltip>
            }
            label='Delete'
            onClick={() => handleDelete(params.row)}
            disabled={!canDelete}
          />,
        ];
      },
    },
  ];

  const activeRoles = roles.filter((r) => r.isActive).length;
  const systemRoles = roles.filter((r) => r.isSystemRole).length;

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant='h4' fontWeight='700' gutterBottom>
              Role Management
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              Manage user roles and permissions
            </Typography>
          </Box>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            Add Role
          </Button>
        </Box>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant='h4' fontWeight='bold'>
                      {roles.length}
                    </Typography>
                    <Typography variant='body2' sx={{ opacity: 0.9 }}>
                      Total Roles
                    </Typography>
                  </Box>
                  <SecurityIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='success.main'
                    >
                      {activeRoles}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Active Roles
                    </Typography>
                  </Box>
                  <CheckCircleIcon
                    sx={{ fontSize: 40, color: "success.main" }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='warning.main'
                    >
                      {systemRoles}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      System Roles
                    </Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 40, color: "warning.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <DataGrid
          rows={roles}
          columns={columns}
          getRowId={(row) => row._id || row.id || String(Math.random())}
          paginationMode='server'
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={roles.length}
          loading={loading}
          autoHeight
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: alpha("#667eea", 0.1),
              borderBottom: `2px solid ${alpha("#667eea", 0.2)}`,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: alpha("#667eea", 0.05),
            },
          }}
        />
      </Paper>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          {editingRole ? "Edit Role" : "Create New Role"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label='Name'
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value.toLowerCase() })
              }
              disabled={!!editingRole}
              required
              helperText='Unique identifier (lowercase, no spaces)'
            />
            <TextField
              label='Display Name'
              value={formData.displayName}
              onChange={(e) =>
                setFormData({ ...formData, displayName: e.target.value })
              }
              required
            />
            <TextField
              label='Description'
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={3}
            />
            <FormControl fullWidth>
              <InputLabel>Permissions</InputLabel>
              <Select
                multiple
                value={formData.permissions}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    permissions: e.target.value as string[],
                  })
                }
                input={<OutlinedInput label='Permissions' />}
                renderValue={(selected) =>
                  `${selected.length} permission(s) selected`
                }
              >
                {permissions
                  .filter((p: Permission) => p.isActive)
                  .map((permission: Permission) => (
                    <MenuItem
                      key={permission._id || permission.id}
                      value={permission._id || permission.id}
                    >
                      <Checkbox
                        checked={formData.permissions.includes(
                          permission._id || permission.id || ""
                        )}
                      />
                      <ListItemText
                        primary={permission.displayName}
                        secondary={`${permission.resource} - ${permission.action}`}
                      />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label={formData.isActive ? "Active" : "Inactive"}
                color={formData.isActive ? "success" : "default"}
                onClick={() =>
                  setFormData({ ...formData, isActive: !formData.isActive })
                }
                sx={{ cursor: "pointer" }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant='contained'>
            {editingRole ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Roles;
